/**
 * =============================================================================
 * setera-web
 * =============================================================================
 * Copyright (c) 2026 HG Digital/Setera
 * Todos os direitos reservados.
 *
 * Este software é confidencial e de propriedade da HG Digital/Setera.
 * Seu uso está sujeito aos termos do contrato de licença.
 * -----
 * Criado em : 2026-07-23 09:35:54
 * Autor : Diego Brock
 * Projeto : setera-web
 * Versão : 1.0.0
 * =============================================================================
 */

import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewChild,
} from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { CommandActionInterface } from "src/app/contracts/CommandAction.interface";
import { PerfilCommandActionInterface } from "src/app/contracts/PerfilCommandAction.interface";
import { PerfilCommandAction } from "src/app/models/PerfilCommandAction.model";
import { ApiBaseService } from "src/app/services/migrate/api.base.service";

/**
 * Aba Comandos do cadastro de Perfil: catálogo com checkboxes e sync bulk.
 */
@Component({
  selector: "app-form-aba-comando",
  templateUrl: "./form-aba-comando.component.html",
  styleUrls: ["./form-aba-comando.component.scss"],
})
export class FormAbaComandoComponent implements OnInit {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  /** Id do perfil legado. Sem id a aba não carrega dados. */
  @Input()
  public perfilId: number;

  /** Quando true, checkboxes ficam somente leitura (tela de visualização). */
  @Input()
  public readOnly = false;

  public hideMasterToggle = false;
  public errorMessage: string;
  public loader = false;

  public displayedColumns: string[] = ["code", "description", "vincular"];

  private apiBaseService: ApiBaseService;
  private readonly paths = new PerfilCommandAction();

  /** Catálogo completo para a tabela (com filtro local). */
  public catalogo: MatTableDataSource<CommandActionInterface>;

  /** Códigos atualmente selecionados (ativos) para o perfil. */
  private selectedCodes = new Set<string>();

  constructor(
    apiBaseService: ApiBaseService,
    private changeDetectorRefs: ChangeDetectorRef
  ) {
    this.apiBaseService = apiBaseService;
    this.catalogo = new MatTableDataSource<CommandActionInterface>([]);
  }

  ngOnInit(): void {
    if (!this.perfilId) {
      return;
    }
    this.loadAll();
  }

  /**
   * Carrega catálogo e seleção atual do perfil em paralelo.
   */
  private async loadAll(): Promise<void> {
    this.loader = true;
    this.errorMessage = null;
    try {
      await Promise.all([this.loadCatalogo(), this.loadSelecao()]);
      this.changeDetectorRefs.detectChanges();
    } catch (error) {
      console.error("loadAll form-aba-comando", error);
      this.errorMessage =
        (error && error.message) || "Falha ao carregar comandos do perfil";
    } finally {
      this.loader = false;
    }
  }

  /**
   * Busca o catálogo de COMMAND_ACTION em GET /command-actions.
   */
  private async loadCatalogo(): Promise<void> {
    const result = await this.apiBaseService.get<CommandActionInterface[]>({
      url: this.paths.catalogoConsultaApiBase,
    });

    this.catalogo = new MatTableDataSource<CommandActionInterface>(
      result || []
    );
    this.catalogo.filterPredicate = (
      data: CommandActionInterface,
      filter: string
    ): boolean => {
      const term = filter.trim().toLowerCase();
      return (
        (data.code || "").toLowerCase().includes(term) ||
        (data.description || "").toLowerCase().includes(term)
      );
    };

    setTimeout(() => {
      this.catalogo.sort = this.sort;
      this.catalogo.paginator = this.paginator;
    }, 300);
  }

  /**
   * Busca vínculos ativos do perfil e marca a seleção local.
   */
  private async loadSelecao(): Promise<void> {
    const params = {
      perfilId: this.perfilId,
      size: 999999,
      page: 0,
    };

    const result = await this.apiBaseService.get<any>({
      url: this.paths.listConsultaApiBase,
      params,
    });

    const content: PerfilCommandActionInterface[] = Array.isArray(result)
      ? result
      : result?.content || [];

    this.selectedCodes = new Set(
      content
        .filter((item) => item.ativo !== false)
        .map((item) => item.commandAction)
        .filter((code) => !!code)
    );
  }

  /**
   * Indica se o comando do catálogo está vinculado ao perfil.
   *
   * @param row item do catálogo
   * @returns true se marcado
   */
  public hasVinculo(row: CommandActionInterface): boolean {
    return this.selectedCodes.has(row.code);
  }

  /**
   * Master toggle: marca/desmarca todos os itens visíveis (filtro atual).
   *
   * @param e evento do slide-toggle
   */
  public onMasterToggleChange = (e): void => {
    if (this.readOnly || this.loader) {
      return;
    }
    const rows = this.catalogo.filteredData || this.catalogo.data;
    rows.forEach((row) => {
      if (e.checked) {
        this.selectedCodes.add(row.code);
      } else {
        this.selectedCodes.delete(row.code);
      }
    });
    this.persistSync();
  };

  /**
   * Indica se todos os itens visíveis estão marcados.
   *
   * @returns true se master deve aparecer marcado
   */
  public handleMasterToggle = (): boolean => {
    const rows = this.catalogo.filteredData || this.catalogo.data;
    if (!rows.length) {
      return false;
    }
    return rows.every((row) => this.selectedCodes.has(row.code));
  };

  /**
   * Toggle individual de um comando; persiste via sync.
   *
   * @param e evento do slide-toggle
   * @param row item do catálogo
   */
  public onCheckboxChange = (e, row: CommandActionInterface): void => {
    if (this.readOnly || this.loader) {
      return;
    }
    if (e.checked) {
      this.selectedCodes.add(row.code);
    } else {
      this.selectedCodes.delete(row.code);
    }
    this.persistSync();
  };

  /**
   * Desabilita toggles durante carga ou em modo somente leitura.
   *
   * @returns true se desabilitado
   */
  public isVinculosDisabled = (): boolean => {
    return this.loader || this.readOnly;
  };

  /**
   * Filtro local no catálogo (código ou descrição).
   *
   * @param event evento de keyup do input
   */
  public applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.catalogo.filter = filterValue.trim().toLowerCase();
    if (this.catalogo.paginator) {
      this.catalogo.paginator.firstPage();
    }
  }

  /**
   * Persiste a seleção atual com PUT .../perfil/{id}/sync.
   * Não envia idUsuario*.
   */
  private async persistSync(): Promise<void> {
    this.loader = true;
    this.errorMessage = null;
    try {
      const commandActions = Array.from(this.selectedCodes);
      const response = await this.apiBaseService.putByUrl<
        PerfilCommandActionInterface[]
      >(`${this.paths.syncConsultaApiBase}/${this.perfilId}/sync`, {
        commandActions,
      });

      this.selectedCodes = new Set(
        (response || [])
          .filter((item) => item.ativo !== false)
          .map((item) => item.commandAction)
          .filter((code) => !!code)
      );
      this.changeDetectorRefs.detectChanges();
    } catch (error) {
      console.error("persistSync form-aba-comando", error);
      this.errorMessage =
        (error && error.message) || "Falha ao sincronizar comandos do perfil";
      // Recarrega seleção do servidor para não deixar UI inconsistente
      try {
        await this.loadSelecao();
      } catch (reloadError) {
        console.error("reloadSelecao após erro", reloadError);
      }
    } finally {
      this.loader = false;
    }
  }
}
